import { useEffect, useState } from "react";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Drawer from "../../components/Drawer";
import BulkImportDrawer from "../../components/BulkImportDrawer";
import { useApi } from "../../hooks/useApi";
import useDevOverride from "../../hooks/useDevOverride";
import {
  listTaskGroups,
  createTaskGroup,
  updateTaskGroup,
  deleteTaskGroup,
  exportTaskGroups,
  bulkImportTaskGroups,
} from "../../api/taskGroupService";
import { listTaskMasters } from "../../api/taskMasterService";
import { paginatedRows } from "../../utils/pagination";
import { showApiError, showToast } from "../../utils/toast";

export default function TaskGroupsPage() {
  const devOverride = useDevOverride();
  const [drawer, setDrawer] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const groups = useApi(() => listTaskGroups({ per_page: 100 }));
  const masters = useApi(() => listTaskMasters({ per_page: 200 }));

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportTaskGroups();
      showToast("success", "Export downloaded");
    } catch (err) {
      showApiError(err);
    } finally {
      setExporting(false);
    }
  };

  const groupRows = paginatedRows(groups.data);
  const masterRows = paginatedRows(masters.data);

  const onDelete = async (g) => {
    const linked = masterRows.filter((m) => m.task_group_id === g.id).length;
    const note = linked > 0
      ? `\n\nWarning: this will also remove ${linked} task master${linked === 1 ? "" : "s"} that belong to it.`
      : "";
    if (!window.confirm(`Delete task group "${g.name}"?${note}`)) return;
    try {
      const res = await deleteTaskGroup(g.id);
      showToast("success", res.message || "Group deleted");
      groups.reload();
      masters.reload();
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Task Groups</h1>
          <div className="page-sub">{groupRows.length} groups · {masterRows.length} task templates</div>
        </div>
        <div className="page-actions">
          {devOverride && (
            <>
              <button className="btn btn-secondary" onClick={handleExport} disabled={exporting}>
                <Icon name="download" size={14} /> {exporting ? "Exporting…" : "Export"}
              </button>
              <button className="btn btn-secondary" onClick={() => setBulkOpen(true)}>
                <Icon name="upload" size={14} /> Bulk import
              </button>
            </>
          )}
          <button className="btn btn-primary" onClick={() => setDrawer({ mode: "create" })}>
            <Icon name="plus" size={14} /> New group
          </button>
        </div>
      </div>

      {groups.loading ? (
        <LoadingBlock rows={2} height={120} />
      ) : groupRows.length === 0 ? (
        <EmptyState icon="folder" title="No task groups" hint="Create groups to organise task masters." />
      ) : (
        <div className="grid-3">
          {groupRows.map((g) => {
            const m = masterRows.filter((t) => t.task_group_id === g.id);
            return (
              <div key={g.id} className="card">
                <div className="card-body">
                  <div className="row gap-sm">
                    <div style={{
                      width: 36, height: 36, borderRadius: 8,
                      background: "var(--primary-50)", color: "var(--primary-700)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <Icon name="folder" size={18} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="t-primary fw-600" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {g.name}
                      </div>
                      <div className="t-muted text-xs">{m.length} task masters</div>
                    </div>
                    <div className="row gap-sm">
                      <button
                        className="icon-btn"
                        title="Edit"
                        onClick={() => setDrawer({ mode: "edit", group: g })}
                      >
                        <Icon name="edit" size={13} />
                      </button>
                      <button
                        className="icon-btn"
                        title="Delete"
                        onClick={() => onDelete(g)}
                        style={{ color: "var(--danger)" }}
                      >
                        <Icon name="trash" size={13} />
                      </button>
                    </div>
                  </div>
                  {m.length > 0 && (
                    <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {m.slice(0, 4).map((mm) => <span key={mm.id} className="badge badge-neutral text-xs">{mm.name}</span>)}
                      {m.length > 4 && <span className="badge badge-neutral text-xs">+{m.length - 4}</span>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <GroupDrawer
        open={!!drawer}
        mode={drawer?.mode}
        group={drawer?.group}
        onClose={() => setDrawer(null)}
        onSaved={() => {
          setDrawer(null);
          groups.reload();
        }}
      />

      {devOverride && (
        <BulkImportDrawer
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          onImported={() => groups.reload()}
          title="Bulk import task groups"
          subtitle="Export current groups, edit in Excel, then upload. Rows with an existing id are updated; blank-id rows create new groups."
          instructions="Step 1 — Download the current data. Step 2 — Edit/add rows. Leave id blank to create new. Step 3 — Upload."
          downloadLabel="Download current data"
          downloadFn={exportTaskGroups}
          importFn={bulkImportTaskGroups}
        />
      )}
    </>
  );
}

function GroupDrawer({ open, mode, group, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(mode === "edit" && group ? group.name : "");
    setErrors({});
  }, [open, mode, group]);

  const submit = async () => {
    setErrors({});
    setSaving(true);
    try {
      if (mode === "edit") {
        await updateTaskGroup(group.id, { name });
        showToast("success", "Group updated");
      } else {
        await createTaskGroup({ name });
        showToast("success", "Group created");
      }
      onSaved?.();
    } catch (err) {
      if (err?.data?.errors) setErrors(err.data.errors);
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={mode === "edit" ? `Edit ${group?.name || "group"}` : "New task group"}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving || !name.trim()}>
            {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Create"}
          </button>
        </>
      }
    >
      <div className="field">
        <label className="field-label">Name</label>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="GST · Compliance · Audit" />
        {errors.name?.[0] && <div className="field-error">{errors.name[0]}</div>}
      </div>
    </Drawer>
  );
}
